import React, { useMemo, useState, useEffect } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Link } from "react-router-dom";
import { Star, MapPin, Search, LoaderCircle } from "lucide-react";

/** Custom price-pill marker icon (Booking-style) */
const makePriceIcon = (price, isHovered = false) =>
    L.divIcon({
        className: "price-pin",
        html: `<div style="
            background: ${isHovered ? "#0284c7" : "#ffffff"};
            color: ${isHovered ? "#ffffff" : "#0f172a"};
            border: 2px solid #0284c7;
            border-radius: 9999px;
            padding: 4px 10px;
            font-weight: 700;
            font-size: 12px;
            box-shadow: 0 4px 12px rgba(2,132,199,.25);
            white-space: nowrap;
            transform: translate(-50%, -100%);
            cursor: pointer;
        ">${price}</div>`,
        iconSize: [0, 0],
        iconAnchor: [0, 0],
    });

function FitBounds({ points, skipFit }) {
    const map = useMap();
    React.useEffect(() => {
        if (skipFit || !points || points.length === 0) return;
        if (points.length === 1) {
            map.setView([points[0].lat, points[0].lng], 13);
        } else {
            const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lng]));
            map.fitBounds(bounds, { padding: [50, 50], maxZoom: 14 });
        }
    }, [points, map, skipFit]);
    return null;
}

/** Tracks user-driven map movement (drag/zoom) and fires onChange with bounds. */
function BoundsTracker({ onChange }) {
    useMapEvents({
        moveend: (e) => {
            const b = e.target.getBounds();
            onChange({
                minLat: b.getSouth(),
                minLng: b.getWest(),
                maxLat: b.getNorth(),
                maxLng: b.getEast(),
            });
        },
    });
    return null;
}

/**
 * Booking-style map view for search results.
 * Renders hotels as price-pill markers on OpenStreetMap.
 * Also provides "Search this area" button when user pans/zooms.
 *
 * Props:
 *   hotels           - array of hotels with lat/lng + price_from
 *   selectedId       - currently highlighted hotel (optional)
 *   onSearchArea     - callback({ minLat, minLng, maxLat, maxLng }) when user clicks "Bu bölgeyi ara"
 *   areaSearching    - boolean: spinner state for the button
 */
export default function HotelsMapView({ hotels = [], selectedId = null, onSearchArea = null, areaSearching = false }) {
    const points = useMemo(
        () => hotels.filter((h) => h.latitude && h.longitude)
            .map((h) => {
                const p = h.price_from || h.min_price || 0;
                return {
                    id: h.id,
                    slug: h.slug,
                    name: h.name,
                    lat: parseFloat(h.latitude),
                    lng: parseFloat(h.longitude),
                    price: p > 0 ? `₺${Math.round(p).toLocaleString("tr-TR")}` : "₺ —",
                    rating: h.rating,
                    stars: h.stars,
                    main_image: h.main_image,
                    destination_name: h.destination_name,
                };
            }),
        [hotels],
    );

    const [hoveredId, setHoveredId] = useState(null);
    const [currentBounds, setCurrentBounds] = useState(null);
    const [initialBoundsLocked, setInitialBoundsLocked] = useState(false);

    // After first fit, lock further auto-fitting (don't snap back when user drags)
    useEffect(() => {
        if (points.length > 0 && !initialBoundsLocked) {
            const t = setTimeout(() => setInitialBoundsLocked(true), 800);
            return () => clearTimeout(t);
        }
    }, [points, initialBoundsLocked]);

    if (points.length === 0) {
        return (
            <div className="w-full h-full min-h-[400px] rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-sm text-slate-500 p-6 text-center" data-testid="map-empty">
                <div>
                    <MapPin className="h-8 w-8 mx-auto mb-2 opacity-40" />
                    Bu aramada konumu olan otel yok.<br />
                    Adminden otellerin enlem/boylam bilgilerini ekleyin.
                </div>
            </div>
        );
    }

    // Initial center: first hotel
    const center = [points[0].lat, points[0].lng];

    return (
        <div className="w-full rounded-2xl overflow-hidden border border-slate-200 dark:border-slate-800 relative z-0" style={{ height: "calc(100vh - 200px)", minHeight: 500 }} data-testid="hotels-map">
            <MapContainer center={center} zoom={11} scrollWheelZoom={true} style={{ height: "100%", width: "100%" }}>
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap' />
                <FitBounds points={points} skipFit={initialBoundsLocked} />
                <BoundsTracker onChange={setCurrentBounds} />
                {points.map((p) => (
                    <Marker
                        key={p.id}
                        position={[p.lat, p.lng]}
                        icon={makePriceIcon(p.price, hoveredId === p.id || selectedId === p.id)}
                        eventHandlers={{
                            mouseover: () => setHoveredId(p.id),
                            mouseout: () => setHoveredId(null),
                        }}
                    >
                        <Popup>
                            <div className="min-w-[200px]" data-testid={`map-popup-${p.id}`}>
                                {p.main_image && (
                                    <img src={p.main_image} alt={p.name} className="w-full h-24 object-cover rounded-md mb-2" />
                                )}
                                <div className="font-bold text-slate-900 text-sm mb-1">{p.name}</div>
                                <div className="text-xs text-slate-500 mb-1.5">{p.destination_name}</div>
                                <div className="flex items-center gap-1 text-xs mb-2">
                                    {p.stars > 0 && (
                                        <span className="text-amber-500">{"★".repeat(p.stars)}</span>
                                    )}
                                    {p.rating > 0 && (
                                        <span className="flex items-center gap-0.5 text-emerald-600 font-bold ml-1">
                                            <Star className="h-3 w-3 fill-emerald-600" /> {p.rating}/10
                                        </span>
                                    )}
                                </div>
                                <div className="flex items-center justify-between gap-2">
                                    <span className="text-base font-bold text-sky-600">{p.price}</span>
                                    <Link
                                        to={`/hotel/${p.id}`}
                                        className="px-3 py-1.5 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-xs font-bold no-underline"
                                    >Detay →</Link>
                                </div>
                            </div>
                        </Popup>
                    </Marker>
                ))}
            </MapContainer>

            {/* Coverage indicator */}
            <div className="absolute top-3 right-3 z-[1000] bg-white dark:bg-slate-900 px-3 py-1.5 rounded-full shadow-lg text-xs font-bold text-slate-700 dark:text-slate-200 flex items-center gap-1.5" data-testid="map-coverage">
                <MapPin className="h-3 w-3 text-sky-500" /> {points.length} / {hotels.length} otel haritada
            </div>

            {/* Search this area button (appears after user pans/zooms) */}
            {onSearchArea && currentBounds && initialBoundsLocked && (
                <button
                    type="button"
                    onClick={() => onSearchArea(currentBounds)}
                    disabled={areaSearching}
                    data-testid="search-this-area"
                    className="absolute top-3 left-1/2 -translate-x-1/2 z-[1000] inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-sky-500 hover:bg-sky-600 disabled:bg-slate-400 text-white text-sm font-bold shadow-2xl shadow-sky-500/40 ring-4 ring-white/70 dark:ring-slate-900/70 transition-transform hover:scale-105"
                >
                    {areaSearching ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                    {areaSearching ? "Aranıyor..." : "Bu bölgeyi ara"}
                </button>
            )}
        </div>
    );
}
